<template>
    <div class="invoicing-display-settings">
        <div class="invoicing-display-settings__columns">
            <Card>
                <Heading
                    :labelTitle="translations.companyInfoTitle"
                    variant="h2"
                    :prefixColoredIcon="{ icon: 'company', color: 'brand' }"
                />
                <p
                    class="invoicing-display-settings__description typographies-paragraph-medium-regular"
                    v-html="companyInfoDescription"
                />
                <InvoicingDisplayFieldsTable
                    :headerLabel="translations.tableInformation"
                    :fields="companyInfoFields"
                    :loading="isLoading"
                    @change="updateValue"
                />
                <CompassFormItem
                    v-if="displayPreferences && !isLoading"
                    class="invoicing-display-settings__staff-form-item"
                    :label="translations.staffLabelLabel"
                    :info="translations.staffLabelInfo"
                >
                    <div class="invoicing-display-settings__staff-labels">
                        <CompassSelect v-model="displayPreferences.assigned_staff_label_id">
                            <CompassDropdownOption
                                v-for="item in activeStaffLabels"
                                :key="item.id"
                                :label="item.label"
                                :value="item.id"
                            />
                        </CompassSelect>
                        <CompassButton
                            :label="translations.staffLabelConfigureButton"
                            :type="CompassEnums.ButtonType.TERTIARY"
                            leftIcon="settings-fill"
                            @click="openStaffLabelsDialog"
                        />
                    </div>
                </CompassFormItem>
                <div class="invoicing-display-settings__actions">
                    <CompassButton
                        :label="$t.__('common.actions.save')"
                        :disabled="isLoading || isSaving"
                        :loading="savingSection === 'companyInfo'"
                        @click="handleSave('companyInfo')"
                    />
                </div>
            </Card>
            <Card>
                <Heading
                    :labelTitle="translations.clientInfoTitle"
                    variant="h2"
                    :prefixColoredIcon="{ icon: 'user-circle', color: 'brand' }"
                />
                <p
                    class="invoicing-display-settings__description typographies-paragraph-medium-regular"
                >
                    {{ translations.clientInfoDescription }}
                </p>
                <InvoicingDisplayFieldsTable
                    :headerLabel="translations.tableClientInformation"
                    :fields="clientInfoFields"
                    :loading="isLoading"
                    @change="updateValue"
                />
                <div class="invoicing-display-settings__actions">
                    <CompassButton
                        :label="$t.__('common.actions.save')"
                        :disabled="isLoading || isSaving"
                        :loading="savingSection === 'clientInfo'"
                        @click="handleSave('clientInfo')"
                    />
                </div>
            </Card>
        </div>
        <Card>
            <Heading
                :labelTitle="translations.productsPaymentsInfoTitle"
                variant="h2"
                :prefixColoredIcon="{ icon: 'list', color: 'brand' }"
            />
            <p
                class="invoicing-display-settings__description typographies-paragraph-medium-regular"
                v-html="translations.productsPaymentsInfoDescription"
            />
            <InvoicingDisplayFieldsTable
                class="invoicing-display-settings__table--margin"
                :headerLabel="translations.productColumnsHeader"
                :fields="productInfoFields"
                :loading="isLoading"
                @change="updateValue"
            />
            <CompassFormItem
                v-if="displayPreferences && !isLoading"
                class="invoicing-display-settings__number-form-item"
                :label="translations.quantityPrecisionLabel"
            >
                <CompassSelect v-model="displayPreferences.quantity_precision">
                    <CompassDropdownOption
                        :label="$t.__('common.labels.default_value')"
                        :value="-1"
                    />
                    <CompassDropdownOption
                        v-for="n in 10"
                        :key="n - 1"
                        :label="getDecimalLabel(n - 1)"
                        :value="n - 1"
                    />
                </CompassSelect>
                <p class="typographies-paragraph-small-regular">
                    <TranslationText
                        :parts="quantityPrecisionDescription"
                        :linkSize="CompassEnums.LinkSize.SMALL"
                    />
                </p>
            </CompassFormItem>
            <InvoicingDisplayFieldsTable
                class="invoicing-display-settings__table--margin-large"
                :headerLabel="translations.paymentInfoHeader"
                :fields="paymentInfoFields"
                :loading="isLoading"
                @change="updateValue"
            />
            <InvoicingDisplayFieldsTable
                class="invoicing-display-settings__table--margin"
                :headerLabel="translations.generalInfoHeader"
                :fields="generalInfoFields"
                :loading="isLoading"
                @change="updateValue"
            />
            <CompassFormItem v-if="!isLoading" :label="translations.signaturesLabel">
                <CompassCheckboxGroup
                    v-model="showSignatures"
                    :direction="CompassEnums.ChoicesListPosition.HORIZONTAL"
                >
                    <CompassCheckbox
                        v-for="signature in signatures"
                        :key="signature.key"
                        :modelValue="signature.key"
                        :label="signature.label"
                    />
                </CompassCheckboxGroup>
            </CompassFormItem>
            <div class="invoicing-display-settings__actions">
                <CompassButton
                    :label="$t.__('common.actions.save')"
                    :disabled="isLoading || isSaving"
                    :loading="savingSection === 'productsPayments'"
                    @click="handleSave('productsPayments')"
                />
            </div>
        </Card>
        <Card>
            <div class="invoicing-display-settings__footer-title">
                <Heading
                    :labelTitle="translations.footerTitle"
                    variant="h2"
                    :prefixColoredIcon="{ icon: 'signature', color: 'brand' }"
                />
                <CompassButton
                    :type="CompassEnums.ButtonType.TERTIARY"
                    :size="CompassEnums.ButtonSize.SMALL"
                    :label="$t.__('common.actions.view_faq')"
                    leftIcon="help-circle-fill"
                    @click="handleGoToFooterFaq"
                />
            </div>
            <template v-if="!isLoading">
                <CompassRadioGroup
                    v-if="displayPreferences"
                    v-model="displayPreferences.footer.mode"
                >
                    <CompassRadio
                        :modelValue="FooterMode.AUTO"
                        :label="translations.footerAuto"
                        :caption="translations.footerAutoCaption"
                    />
                    <CompassRadio
                        :modelValue="FooterMode.MANUAL"
                        :label="translations.footerManual"
                    />
                </CompassRadioGroup>
                <div v-if="isManualFooter">
                    <CompassAlert
                        class="invoicing-display-settings__footer-alert"
                        :type="CompassEnums.AlertType.WARNING"
                        :size="CompassEnums.AlertSize.SMALL"
                        :description="translations.footerManualWarning"
                        :showClose="false"
                    />
                    <CompassTabs
                        v-if="displayPreferences"
                        :key="languagesStore.languages.length"
                        v-model="currentTab"
                    >
                        <CompassTabPane
                            :label="$t.__('common.labels.by_default')"
                            name="default"
                            icon="flag-fill"
                        >
                            <RichText
                                v-model="manualFooter.value"
                                :options="optionsRichText"
                                class="custom-richtext"
                            />
                        </CompassTabPane>
                        <CompassTabPane
                            v-for="language in languagesStore.languages"
                            :key="language.id"
                            :label="language.lang"
                            :name="language.lang"
                            :customIcon="getLanguageIcon(language.flag)"
                        >
                            <RichText
                                v-model="getFooterTranslation(language.id).value"
                                :options="optionsRichText"
                                class="custom-richtext"
                            />
                        </CompassTabPane>
                    </CompassTabs>
                </div>
            </template>
            <div class="invoicing-display-settings__actions">
                <CompassButton
                    :label="$t.__('common.actions.save')"
                    :disabled="isLoading || isSaving"
                    :loading="savingSection === 'footer'"
                    @click="handleSave('footer')"
                />
            </div>
        </Card>
    </div>
</template>

<script setup lang="ts">
import { ref, inject, provide, onMounted, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { Translator } from 'types/injects/translator.type';
import { PushToast } from 'types/injects/pushToast.type';
import Card from 'vcomponents/common/card/Card.vue';
import Heading from 'vcomponents/common/Heading/Heading.vue';
import InvoicingDisplayFieldsTable from 'vcomponents/settings/invoicing/InvoicingDisplayFieldsTable.vue';
import TranslationText from 'vcomponents/common/TranslationText.vue';
import { useTranslationLinkParser } from 'composables/useTranslationLinkParser';
import {
    getMetadata,
    getDisplayPreferences,
    updateDisplayPreferences,
    type InvoicingSettingsMetadata,
    type DisplayPreferences,
    type ManualFooter,
    type FooterTranslation,
    FooterMode,
} from 'api/settings/invoicing/invoicingDisplaySettingsService';
import type { AssignedStaffLabel } from 'api/settings/assignedStaffLabelsService';
import { useLanguagesStore } from 'js/vue/stores/languages/languagesStore';
import RichText from 'vcomponents/richtext/RichText.vue';
import { replaceTagsWithSpan, replaceSpansWithTags } from 'Helpers/richTextHelper.js';
import { captureException } from '@sentry/vue';

// DisplayPreferences has assigned_staff_label_id (number) and footer (object), so keyof DisplayPreferences makes the value type too wide.
// narrow the parameter type to only accept boolean fields
type BooleanDisplayPref = {
    [K in keyof DisplayPreferences]: DisplayPreferences[K] extends boolean ? K : never;
}[keyof DisplayPreferences];

interface FieldEntry {
    field: BooleanDisplayPref;
    label: string;
    value: boolean;
    isForced: boolean;
}

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

const store = useStore();
const languagesStore = useLanguagesStore();

type SaveSection = 'companyInfo' | 'clientInfo' | 'productsPayments' | 'footer';

const isLoading = ref(true);
const savingSection = ref<SaveSection | null>(null);
const isSaving = computed((): boolean => savingSection.value !== null);
const metadata = ref<InvoicingSettingsMetadata | null>(null);
const displayPreferences = ref<DisplayPreferences | null>(null);
const currentTab = ref('default');

const optionsRichText = ['Bold', 'Divider', 'CustomTagsAndFields'];

const providedTags = computed((): { category: string; name: string; key: string }[] =>
    (metadata.value?.custom_tags ?? []).map((tag) => ({
        category: tag.category,
        name: tag.description,
        key: tag.name,
    })),
);
provide('customTagsAndFields', providedTags);

const SEMIBOLD_CLASS = 'typographies-paragraph-medium-semibold';

const { parseTranslation } = useTranslationLinkParser();

const translations: Record<string, string> = {
    companyInfoTitle: $t.__('settings.invoicingDisplay.companyInfo.title'),
    clientInfoDescription: $t.__('settings.invoicingDisplay.clientInfo.description'),
    clientInfoTitle: $t.__('settings.invoicingDisplay.clientInfo.title'),
    productsPaymentsInfoTitle: $t.__('settings.invoicingDisplay.productsPaymentsInfo.title'),
    productsPaymentsInfoDescription: $t.__(
        'settings.invoicingDisplay.productsPaymentsInfo.description',
        { boldStart: '<span class="typographies-paragraph-medium-semibold">', boldEnd: '</span>' },
    ),
    productColumnsHeader: $t.__('settings.invoicingDisplay.productColumns.header'),
    quantityPrecisionLabel: $t.__('settings.invoicingDisplay.quantityPrecision.label'),
    paymentInfoHeader: $t.__('settings.invoicingDisplay.paymentInfo.header'),
    generalInfoHeader: $t.__('settings.invoicingDisplay.generalInfo.header'),
    signaturesLabel: $t.__('settings.invoicingDisplay.signatures.label'),
    footerTitle: $t.__('settings.invoicingDisplay.footer.title'),
    footerAuto: $t.__('settings.invoicingDisplay.footer.auto'),
    footerManual: $t.__('settings.invoicingDisplay.footer.manual'),
    footerAutoCaption: $t.__('settings.invoicingDisplay.footer.autoCaption'),
    footerManualWarning: $t.__('settings.invoicingDisplay.footer.manualWarning'),
    tableInformation: $t.__('settings.invoicingDisplay.table.information'),
    tableClientInformation: $t.__('settings.invoicingDisplay.table.clientInformation'),
    staffLabelLabel: $t.__('settings.invoicingDisplay.staffLabel.label'),
    staffLabelInfo: $t.__('settings.invoicingDisplay.staffLabel.info'),
    staffLabelConfigureButton: $t.__('settings.invoicingDisplay.staffLabel.configureButton'),
};

const quantityPrecisionDescription = parseTranslation(
    $t.__('settings.invoicingDisplay.quantityPrecision.description'),
);

const companyInfoDescription = $t.__('settings.invoicingDisplay.companyInfo.description', {
    startBold: `<span class="${SEMIBOLD_CLASS}">`,
    endBold: '</span>',
});

const buildFieldEntries = (fields: BooleanDisplayPref[]): FieldEntry[] => {
    return fields.map((field) => {
        return {
            field,
            label: $t.__(`settings.invoicingDisplay.field.${field}`),
            value: displayPreferences.value?.[field] ?? false,
            isForced:
                (metadata.value?.enforce_display_rules &&
                    metadata.value.forced_prefs.includes(field)) ??
                false,
        };
    });
};

const companyInfoKeys: BooleanDisplayPref[] = [
    'show_assigned_staff_identity',
    'show_assigned_staff_email',
    'show_assigned_staff_phone',
    'show_assigned_staff_mobile',
    'show_assigned_staff_fax',
    'show_address',
];

const clientInfoKeys: BooleanDisplayPref[] = [
    'show_contact_name',
    'show_company_phone_number',
    'show_vat_number',
    'show_siret',
    'show_siren',
    'show_company_reference',
];

const productInfoKeys: BooleanDisplayPref[] = [
    'show_reference_column',
    'show_notes_column',
    'show_unit_cost_column',
    'show_taxes_column',
    'show_quantity_column',
    'show_amount_column',
    'show_image_column',
    'show_discount_column',
    'show_barcode_values',
    'show_barcode_images',
];

const paymentInfoKeys: BooleanDisplayPref[] = [
    'show_payment_terms',
    'show_payment_deadlines',
    'show_payment_methods',
    'show_deposit_details',
    'show_bank_account',
    'show_check_label',
];

const generalInfoKeys: BooleanDisplayPref[] = [
    'show_tax_basis',
    'show_vat_mention',
    'show_sale_type',
    'show_parent_document',
    'show_payment_date',
    'show_total',
    'show_tax_details',
    'show_item_count',
    'show_item_weight',
    'show_prices_on_delivery',
];

const signatureKeys: BooleanDisplayPref[] = [
    'show_signature_on_estimate',
    'show_signature_on_order',
    'show_signature_on_delivery',
];

const getDecimalLabel = (count: number): string => {
    return $t.n__('settings.invoicingDisplay.quantityPrecision.decimal', null, count, count);
};

const showSignatures = ref<typeof signatureKeys>([]);

const signatures = signatureKeys.map((key) => ({
    key,
    label: $t.__(`settings.invoicingDisplay.field.${key}`),
}));

const companyInfoFields = computed((): FieldEntry[] => buildFieldEntries(companyInfoKeys));

const clientInfoFields = computed((): FieldEntry[] => buildFieldEntries(clientInfoKeys));

const productInfoFields = computed((): FieldEntry[] => buildFieldEntries(productInfoKeys));

const paymentInfoFields = computed((): FieldEntry[] => buildFieldEntries(paymentInfoKeys));

const generalInfoFields = computed((): FieldEntry[] => buildFieldEntries(generalInfoKeys));

const isManualFooter = computed((): boolean => {
    return displayPreferences.value?.footer.mode === FooterMode.MANUAL;
});

const manualFooter = computed((): ManualFooter => {
    return displayPreferences.value?.footer as ManualFooter;
});

const defaultTranslation: FooterTranslation = { lang_id: 0, value: '' };

const getFooterTranslation = (langId: number): FooterTranslation => {
    const footer = manualFooter.value;
    if (!footer) {
        return defaultTranslation;
    }
    if (footer.value === undefined) {
        footer.value = '';
    }
    if (!footer.translations) {
        footer.translations = [];
    }
    let translation = footer.translations.find((entry) => entry.lang_id === langId);
    if (!translation) {
        translation = { lang_id: langId, value: '' };
        footer.translations.push(translation);
    }
    return translation;
};

const activeStaffLabels = computed((): AssignedStaffLabel[] | undefined => {
    return metadata.value?.assigned_staff_labels.filter((item) => item.is_active);
});

const fetchInitialData = async (): Promise<void> => {
    isLoading.value = true;
    try {
        const [metadataResponse, preferencesResponse] = await Promise.all([
            getMetadata(),
            getDisplayPreferences(),
        ]);
        metadata.value = metadataResponse.data;
        displayPreferences.value = {
            ...preferencesResponse.data,
            quantity_precision: preferencesResponse.data.quantity_precision ?? -1,
        };
        if (displayPreferences.value.footer.mode === FooterMode.MANUAL) {
            const tags = providedTags.value;
            displayPreferences.value.footer.value = replaceTagsWithSpan(
                displayPreferences.value.footer.value,
                tags,
            );
            displayPreferences.value.footer.translations =
                displayPreferences.value.footer.translations.map((t) => ({
                    ...t,
                    value: replaceTagsWithSpan(t.value, tags),
                }));
        }
        showSignatures.value = signatureKeys.filter(
            (key) => preferencesResponse.data[key as keyof DisplayPreferences],
        );
        languagesStore.languages = metadataResponse.data.languages;
    } catch {
        $pushToast({
            type: CompassEnums.ToastType.ERROR,
            text: $t.__('common.labels.toast.error'),
        });
    } finally {
        isLoading.value = false;
    }
};

const updateValue = (newValue: boolean, field: keyof DisplayPreferences): void => {
    if (displayPreferences.value) {
        (displayPreferences.value[field] as boolean) = newValue;
    }
};

const openStaffLabelsDialog = (): void => {
    store.dispatch('popupPanel/open', {
        key: 'assignedStaffLabelsModal',
        props: {
            callback: fetchInitialData,
        },
    });
};

const pickBooleanFields = (fields: BooleanDisplayPref[]): Partial<DisplayPreferences> => {
    if (!displayPreferences.value) {
        return {};
    }
    const picked = {} as Record<BooleanDisplayPref, boolean>;
    for (const field of fields) {
        picked[field] = displayPreferences.value[field];
    }
    return picked;
};

const buildFooterPayload = (): Partial<DisplayPreferences> => {
    if (!displayPreferences.value) {
        return {};
    }
    const footerPref = displayPreferences.value.footer;
    if (footerPref.mode !== FooterMode.MANUAL) {
        return { footer: { mode: FooterMode.AUTO } };
    }
    const tags = providedTags.value;
    return {
        footer: {
            mode: FooterMode.MANUAL,
            value: replaceSpansWithTags(footerPref.value, tags) ?? '',
            translations: (footerPref.translations ?? [])
                .filter((entry) => Boolean(entry.value))
                .map((entry) => ({
                    ...entry,
                    value: replaceSpansWithTags(entry.value, tags) ?? '',
                })),
        },
    };
};

const buildSectionPayload = (section: SaveSection): Partial<DisplayPreferences> => {
    if (!displayPreferences.value) {
        return {};
    }
    switch (section) {
        case 'companyInfo':
            return {
                ...pickBooleanFields(companyInfoKeys),
                assigned_staff_label_id: displayPreferences.value.assigned_staff_label_id,
            };
        case 'clientInfo':
            return pickBooleanFields(clientInfoKeys);
        case 'productsPayments':
            return {
                ...pickBooleanFields(productInfoKeys),
                ...pickBooleanFields(paymentInfoKeys),
                ...pickBooleanFields(generalInfoKeys),
                ...pickBooleanFields(signatureKeys),
                quantity_precision:
                    displayPreferences.value.quantity_precision === -1
                        ? null
                        : displayPreferences.value.quantity_precision,
            };
        case 'footer':
            return buildFooterPayload();
    }
};

const handleSave = async (section: SaveSection): Promise<void> => {
    if (!displayPreferences.value) {
        return;
    }
    savingSection.value = section;
    try {
        await updateDisplayPreferences(buildSectionPayload(section));
        $pushToast({
            type: CompassEnums.ToastType.SUCCESS,
            text: $t.__('settings.invoicingDisplay.toast.saveSuccess'),
        });
    } catch (error) {
        $pushToast({
            type: CompassEnums.ToastType.ERROR,
            text: $t.__('settings.invoicingDisplay.toast.saveError'),
        });
        captureException(error);
    } finally {
        savingSection.value = null;
    }
};

const getLanguageIcon = (flag: string): string => {
    return `/ressources/public/img/flags/${flag ?? 'blank'}.png`;
};

const handleGoToFooterFaq = (): void => {
    window.open(
        'https://help.sellsy.com/fr/articles/5871533-modifier-le-pied-de-page-de-mes-documents',
        '_blank',
    );
};

watch(showSignatures, (checkedKeys) => {
    if (!displayPreferences.value) {
        return;
    }
    for (const key of signatureKeys) {
        (displayPreferences.value[key as keyof DisplayPreferences] as boolean) =
            checkedKeys.includes(key);
    }
});

onMounted(() => {
    fetchInitialData();
});
</script>

<style lang="scss" scoped>
.invoicing-display-settings {
    &__columns {
        align-items: start;
        display: flex;
        gap: var(--spacing-4);
        margin-bottom: var(--spacing-4);

        @media screen and (max-width: 1199px) {
            align-items: stretch;
            flex-direction: column;
        }

        :deep() {
            .card-container {
                flex: 0 1 50%;
                min-width: 0;

                & + .card-container {
                    margin-top: 0;
                }
            }
        }
    }

    &__description {
        color: var(--colors-common-text-subtle-default);
        margin: 0 0 var(--spacing-3);
    }

    &__actions {
        display: flex;
        justify-content: end;
        padding: var(--spacing-4) 0 0;
    }

    &__staff-form-item {
        margin: var(--spacing-4) 0 0;
    }

    &__staff-labels {
        align-items: center;
        display: flex;
        gap: var(--spacing-2);

        :deep(.compass-select) {
            max-width: 260px;
            width: 100%;
        }
    }

    &__table {
        &--margin {
            margin: 0 0 var(--spacing-4);
        }

        &--margin-large {
            margin: 0 0 var(--spacing-5);
        }
    }

    &__number-form-item {
        margin: 0 0 var(--spacing-5);

        p {
            color: var(--colors-common-text-subtle-default);
            margin: 0;
        }

        :deep(.compass-select) {
            max-width: 260px;
            width: 100%;
        }
    }

    &__footer {
        &-alert {
            margin-top: var(--spacing-3);
        }
        &-title {
            align-items: flex-start;
            display: flex;
            justify-content: space-between;
            .compass-button {
                margin-top: var(--spacing-0);
            }
        }
    }
}
</style>
